import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Briefcase, Users, Globe, Heart, Star, ChevronDown, ChevronUp, MapPin, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

interface JobPosition {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  experience: string;
  description: string;
  requirements: string[];
  responsibilities: string[];
}

const openPositions: JobPosition[] = [
  {
    id: 'senior-tour-manager',
    title: 'Senior Tour Manager',
    department: 'Operations',
    location: 'Kampala, Uganda',
    type: 'Full-time',
    experience: '5+ years',
    description: 'Lead and oversee our tour operations team while ensuring exceptional experiences for our clients.',
    requirements: [
      'Bachelor\'s degree in Tourism, Hospitality, or related field',
      'Proven experience in tour management and team leadership',
      'Excellent communication and problem-solving skills',
      'Strong knowledge of Ugandan tourism industry',
      'Fluent in English; knowledge of other languages is a plus'
    ],
    responsibilities: [
      'Manage and coordinate tour operations across Uganda',
      'Lead and mentor a team of tour guides and coordinators',
      'Develop and implement tour quality standards',
      'Handle client feedback and maintain high satisfaction rates',
      'Collaborate with local partners and vendors'
    ]
  },
  {
    id: 'marketing-specialist',
    title: 'Digital Marketing Specialist',
    department: 'Marketing',
    location: 'Remote/Kampala',
    type: 'Full-time',
    experience: '3+ years',
    description: 'Drive our digital marketing initiatives to showcase Uganda\'s beauty to the world.',
    requirements: [
      'Proven experience in digital marketing and social media management',
      'Strong understanding of SEO and content marketing',
      'Experience with analytics tools and data-driven marketing',
      'Creative mindset with excellent visual and written communication skills',
      'Knowledge of tourism industry preferred'
    ],
    responsibilities: [
      'Develop and execute digital marketing strategies',
      'Manage social media presence and content calendar',
      'Create engaging content for various platforms',
      'Analyze marketing metrics and optimize campaigns',
      'Collaborate with content creators and photographers'
    ]
  }
];

const benefits = [
  {
    icon: Globe,
    title: 'Travel Opportunities',
    description: 'Explore Uganda\'s most beautiful destinations with travel benefits and familiarization trips.'
  },
  {
    icon: Users,
    title: 'Inclusive Culture',
    description: 'Join a diverse team that celebrates different perspectives and promotes personal growth.'
  },
  {
    icon: Star,
    title: 'Career Growth',
    description: 'Continuous learning opportunities, mentorship programs, and clear career progression paths.'
  },
  {
    icon: Heart,
    title: 'Comprehensive Benefits',
    description: 'Health insurance, paid time off, retirement plans, and other competitive benefits.'
  }
];

const Careers = () => {
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: '',
    resume: null as File | null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) formBody.append(key, value);
      });

      const response = await fetch('/', {
        method: 'POST',
        body: formBody
      });

      if (response.ok) {
        window.location.href = '/success';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f"
            alt="Team collaboration"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">Join Our Team</h1>
            <p className="text-xl text-white/90 mb-6">
              Help us showcase Uganda's beauty to the world while building a meaningful career in tourism.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Why Work With Us</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl border border-gray-200 hover:border-ta-green/50 transition-all duration-300 hover:shadow-lg group"
              >
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <benefit.icon className="h-6 w-6 text-ta-green" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Open Positions</h2>

          <div className="space-y-4">
            {openPositions.map((job) => (
              <motion.div
                key={job.id}
                layout
                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Briefcase className="h-4 w-4 text-ta-green mr-1" />
                          {job.department}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-ta-green mr-1" />
                          {job.location}
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-ta-green mr-1" />
                          {job.type}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                      className="text-ta-green hover:text-ta-green-dark"
                    >
                      {expandedJob === job.id ? (
                        <ChevronUp className="h-6 w-6" />
                      ) : (
                        <ChevronDown className="h-6 w-6" />
                      )}
                    </button>
                  </div>

                  <AnimatePresence>
                    {expandedJob === job.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-6 overflow-hidden"
                      >
                        <p className="text-gray-600 mb-6">{job.description}</p>

                        <div className="space-y-6">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h4>
                            <ul className="list-disc pl-5 space-y-2 text-gray-600">
                              {job.requirements.map((req, index) => (
                                <li key={index}>{req}</li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Responsibilities</h4>
                            <ul className="list-disc pl-5 space-y-2 text-gray-600">
                              {job.responsibilities.map((resp, index) => (
                                <li key={index}>{resp}</li>
                              ))}
                            </ul>
                          </div>

                          <button
                            onClick={() => {
                              setFormData(prev => ({ ...prev, position: job.title }));
                              document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="bg-ta-green text-white px-6 py-2 rounded-lg hover:bg-ta-green-dark transition-colors"
                          >
                            Apply Now
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Apply Now</h2>

          <form
            name="career-application-form"
            method="POST"
            data-netlify="true"
            encType="multipart/form-data"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <input type="hidden" name="form-name" value="career-application-form" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Position *</label>
                <select
                  name="position"
                  required
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                >
                  <option value="">Select a position</option>
                  {openPositions.map(job => (
                    <option key={job.id} value={job.title}>{job.title}</option>
                  ))}
                  <option value="other">Other Opportunities</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Years of Experience *</label>
                <input
                  type="text"
                  name="experience"
                  required
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                  placeholder="e.g., 3 years in tourism industry"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Cover Letter</label>
                <textarea
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                  placeholder="Tell us about yourself and why you'd be a great fit..."
                ></textarea>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Resume/CV *</label>
                <input
                  type="file"
                  name="resume"
                  required
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setFormData({ ...formData, resume: e.target.files?.[0] || null })}
                  className="mt-1 block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-ta-green/10 file:text-ta-green
                    hover:file:bg-ta-green/20"
                />
                <p className="mt-1 text-sm text-gray-500">PDF, DOC, or DOCX up to 10MB</p>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-ta-green text-white px-8 py-3 rounded-lg hover:bg-ta-green-dark transition-colors disabled:opacity-50 flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Careers;